const Link = dynamic(() => import('next/link'));
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Rating } from 'react-simple-star-rating';
import { useMediaQuery } from '../hooks/mediaQuery';
import { useLazyQuery, useMutation } from '@apollo/client';
import { FETCH_GOOGLE_STATS, UPDATE_GOOGLE_STATS } from '../api/reviews';

const row1 = [
  {
    id: 'freeCancellation',
    snippet: 'Free Cancellation',
    icon: '/free-cancellations.svg',
    link: '/faq'
  },
  {
    id: 'customerSupport',
    snippet: 'Support Available 7 Days a Week',
    icon: '/customer-support.svg',
    link: '/contact'
  },
  {
    id: 'tours',
    snippet: 'Small Group Tours',
    icon: '/small-tours.svg',
    link: '/tours'
  },
  {
    id: 'cities',
    snippet: '22 Cities Across North America',
    icon: '/globe.svg',
    link: '/cities'
  }
];
const Trustbar = () => {
  const [googleRating, setGoogleRating] = useState(0);
  const [googleCount, setGoogleCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [dataSource, setDataSource] = useState<'dom' | 'hasura' | 'fallback'>(
    'dom'
  );
  const isMobile = useMediaQuery(768);
  const isTablet = useMediaQuery(1024);
  const starRating = isMobile ? 14 : isTablet ? 18 : 21;

  const [fetchStats, { data: statsData }] = useLazyQuery(FETCH_GOOGLE_STATS);
  const [updateStats] = useMutation(UPDATE_GOOGLE_STATS);
  const row_id = '57438037-f8d7-4e9f-af36-ff125ba8a89d';
  // dev row_id d9047a6d-ea79-40d8-9edd-dd6a191509c1
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;
    let previousStats = { rating: 0, count: 0 };
    let validDataFound = false;

    const checkData = () => {
      const ratingEl = document.querySelector('.es-rating-value');
      const countEl = document.querySelector('.es-badge-reviews-count');

      if (ratingEl?.textContent && countEl?.textContent) {
        const rating = parseFloat(ratingEl?.textContent?.trim());
        const text = countEl?.textContent?.replace(/[(),]/g, '')?.trim();
        const number = parseInt(text?.match(/\d+/)?.[0] || '0');

        if (!isNaN(rating) && !isNaN(number) && rating > 0 && number > 1000) {
          return { rating, count: number };
        }
      }
      return { rating: 0, count: 0 };
    };

    const updateHasuraStats = async (rating: number, count: number) => {
      try {
        if (row_id) {
          const hasuraRes = await fetchStats({ variables: { id: row_id } });
          const existingStats =
            hasuraRes?.data?.boatnew_organizations_stats_by_pk;
          const existingRating = parseFloat(
            existingStats?.google_reviews_rating || '0'
          );
          const existingCount = parseInt(
            existingStats?.google_reviews_count || '0'
          );

          if (rating > existingRating || count > existingCount) {
            await updateStats({
              variables: {
                id: row_id,
                google_reviews_rating: rating?.toString(),
                google_reviews_count: count?.toString()
              }
            });
          }
        }
      } catch (error) {
        console.error('Error updating Hasura:', error);
      }
    };

    const interval = setInterval(async () => {
      attempts++;
      const currentStats = checkData();
      const statsStabilized =
        attempts > 1 &&
        currentStats.rating === previousStats.rating &&
        currentStats.count === previousStats.count &&
        currentStats.rating > 0 &&
        currentStats.count > 0;

      if (statsStabilized || attempts >= maxAttempts) {
        clearInterval(interval);
        setIsLoading(false);

        if (statsStabilized) {
          setGoogleRating(currentStats.rating);
          setGoogleCount(currentStats.count);
          setDataSource('dom');
          validDataFound = true;
          console.log(
            'Stabilized DOM data found:',
            currentStats.rating,
            currentStats.count
          );
          await updateHasuraStats(currentStats.rating, currentStats.count);
        } else {
          try {
            if (row_id) {
              const res = await fetchStats({ variables: { id: row_id } });
              const stats = res?.data?.boatnew_organizations_stats_by_pk;
              if (stats) {
                setGoogleRating(
                  parseFloat(stats?.google_reviews_rating || '4.9')
                );
                setGoogleCount(parseInt(stats?.google_reviews_count || '5500'));
                setDataSource('hasura');
              } else {
                setGoogleRating(4.9);
                setGoogleCount(5500);
                setDataSource('fallback');
              }
            } else {
              setGoogleRating(4.9);
              setGoogleCount(5500);
              setDataSource('fallback');
            }
          } catch (err) {
            console.error('Failed to fetch from Hasura:', err);
            setGoogleRating(4.9);
            setGoogleCount(5500);
            setDataSource('fallback');
          }
        }
      }

      previousStats = currentStats;
    }, 3000);

    return () => clearInterval(interval);
  }, []);
  const formatCount = (count: number) => {
    return count >= 1000
      ? (count / 1000)?.toFixed(1)?.replace(/\.0$/, '') + 'k'
      : count;
  };
  return (
    <section id="trusbar" className={`md:mt-8 lg:mt-1 xl:mt-0   `}>
      <div
        id="experience"
        className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-2 w-full max-w-screen-lg mx-auto text-white "
      >
        {row1?.map(dt => (
          <div key={dt?.id} className="flex items-start justify-center">
            {' '}
            <Link href={dt?.link} className="w-full" target="_blank">
              <div className="flex flex-col items-center gap-3 2xl:gap-5 text-center px-2 sm:px-4">
                <div className="h-[60px] md:h-[73px] xl:h-[82px] shrink-0">
                  <Image
                    src={dt?.icon}
                    width={40}
                    height={40}
                    alt="trust us"
                    priority
                    className="w-full h-full object-contain"
                  />
                </div>

                <p className="text-[14px] lg:text-[17px] max-w-[130px] text-primary font-[600] leading-tight">
                  {dt?.snippet}
                </p>
              </div>
            </Link>
          </div>
        ))}
      </div>

      <div className="w-full flex flex-col mx-auto my-8 ">
        <Image
          src="/dot-in-lines.svg"
          width={550}
          height={10}
          alt="dot lines"
          className=" mx-auto xmsm:w-[90%] max-w-[532px] h-[6px]"
        />
      </div>
      <div className="flex justify-between md:justify-center gap-1 md:gap-4 xl:gap-8 w-full flex-row  pb-6 md:py-8 ">
        <Image
          src="/tripadvisor-badge.png"
          width={145}
          height={162}
          alt="trip advisor badge"
          className="w-[79px] h-[83px] md:w-[107px] md:h-[112px] xl:w-[155px] xl:h-[162px]"
        />{' '}
        <div
          onClick={() => {
            if (dataSource === 'dom') {
              const reviewButton = document.querySelector(
                '.es-badge-rating-container'
              );
              (reviewButton as HTMLElement)?.click();
            } else {
              const elfsightApp = document.querySelector(
                '.elfsight-app-1df5d44e-350f-49b0-83f3-2974a31153c1'
              );
              if (elfsightApp) {
                (elfsightApp as HTMLElement).scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
                (elfsightApp as HTMLElement).focus();
              }
            }
          }}
          className="cursor-pointer shrink-0 flex flex-col justify-center items-center w-[79px] h-[83px] md:w-[113px] md:h-[112px] xl:w-[155px] xl:h-[162px]"
        >
          <Image
            src="/Google.svg"
            width={145}
            height={162}
            alt="google icon"
            className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] xl:w-[79px] xl:h-[79px] "
          />
          <div className="hidden">
            <div className="elfsight-app-7d8a08b2-02a3-445c-96cf-3d21070772e8"></div>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="text-primary text-[20px] md:text-[30px] font-[700] leading-none">
                {googleRating}
              </div>
              <div className="shrink-0">
                <Rating
                  initialValue={googleRating}
                  readonly
                  iconsCount={5}
                  size={starRating}
                  allowHover={false}
                  disableFillHover
                  fillColor="orange"
                  emptyColor="#9B9B9B"
                  allowFraction
                  SVGstyle={{ display: 'inline-block', marginRight: '2px' }}
                />
              </div>
              <div className="text-primary text-[10px] md:text-[13px] font-[400]">
                ({formatCount(googleCount)})
              </div>
            </>
          )}
        </div>
        <Image
          src="/award-winner.png"
          width={145}
          height={162}
          alt="viator award winner badge"
          className="w-[79px] h-[83px] md:w-[100px] md:h-[112px] xl:w-[155px] xl:h-[162px]"
        />
      </div>
    </section>
  );
};

export default Trustbar;
